import React, { useEffect } from "react";
import { Link } from "react-router-dom";
import { useSelectedLocation } from "../contexts/SelectedLocationContext";
import { Tab } from "../types/Tab";
import TermsAndAgreementsModal from "../components/TermsAndAgreementsModal";
import PageSection from "../components/PageSection";
import PageContainer, { GapSize } from "../components/PageContainer";
import OrderWrapper from "../components/OrderWrapper";
import { useDocumentMeta } from "../hooks/useDocumentMeta";

const ContactPage: React.FC = () => {
  const { setDomain, setCategory, setProduct } = useSelectedLocation();
  useDocumentMeta({
    title: "Contact Us",
    description:
      "Get in touch with The Americana Company — questions, orders, and support.",
  });

  useEffect(() => {
    setDomain(Tab.Contact.toString());
    setCategory(null);
    setProduct(null);
  }, [setDomain, setCategory, setProduct]);

  useEffect(() => {
    window.scrollTo(0, 0);
  }, []);

  return (
    <PageContainer gapSize={GapSize.Small}>
      <PageSection className="hero">
        <h1>Contact Us</h1>
        <p>Have questions or need assistance? We&apos;re here to help.</p>
      </PageSection>

      <PageSection className="flex flex-col items-center py-4">
        <div className="page-body">
          <OrderWrapper>
            <div className="flex flex-col gap-4">
              <h2 className="playfair-text text-2xl text-brown-dark">
                Get In Touch
              </h2>
              <p className="lora-text text-gray-600 leading-relaxed">
                Reach out to us via any of the methods below and we&apos;ll get
                back to you as soon as possible. Thank you and God Bless
                America!
              </p>

              <div className="order-divider" />

              <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                <div className="flex flex-col gap-1">
                  <span className="order-input-label">Email</span>
                  <a
                    href="mailto:support@americanaandcompany.com"
                    className="page-card-link break-words"
                  >
                    support@americanaandcompany.com
                  </a>
                </div>
                <div className="flex flex-col gap-1">
                  <span className="order-input-label">Response Time</span>
                  <span className="lora-text text-gray-600">
                    Usually within one business day.
                  </span>
                </div>
              </div>
            </div>
          </OrderWrapper>

          {/* Most contact emails are one of these four questions — answering
              them here saves the shopper (and support) a round trip. */}
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
            <div className="page-card">
              <h2 className="page-card-title">Where&apos;s my order?</h2>
              <p className="page-card-text">
                Look it up with your order ID and checkout email.
              </p>
              <Link to="/orders/track" className="page-card-link text-sm">
                Track an order
              </Link>
            </div>
            <div className="page-card">
              <h2 className="page-card-title">What size do I need?</h2>
              <p className="page-card-text">
                Our apparel is a unisex fit — measurements and women&apos;s-fit
                guidance are in the guide.
              </p>
              <Link to="/size-guide" className="page-card-link text-sm">
                Size guide
              </Link>
            </div>
            <div className="page-card">
              <h2 className="page-card-title">
                Can I cancel or return something?
              </h2>
              <p className="page-card-text">
                Cancellations are free before production begins, and damaged
                items are always covered.
              </p>
              <Link to="/returns" className="page-card-link text-sm">
                Returns &amp; cancellations
              </Link>
            </div>
            <div className="page-card">
              <h2 className="page-card-title">Something else?</h2>
              <p className="page-card-text">
                Shipping times, payments, and the rest of the common questions.
              </p>
              <Link to="/faq" className="page-card-link text-sm">
                Read the FAQ
              </Link>
            </div>
          </div>

          <div className="flex justify-center">
            <TermsAndAgreementsModal />
          </div>
        </div>
      </PageSection>
    </PageContainer>
  );
};

export default ContactPage;
